import apiAgent from "../api/apiAgent";
import { useState } from "react";
import { tMsg } from "../components/ToastNotifications";
import { Button } from "../components/Button";
import { isValidEmail } from "../utils";
import { Link } from "react-router-dom";
import { Loader } from "../components/Loader";
import { VerifyPinField } from "../components/MFA/utils";
import _ from "lodash";

interface IResetPasswordEmail {
  email: string;
  mfa_code: number | null;
}

const RequestReset = (): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [formData, setFormData] = useState<IResetPasswordEmail>({
    email: "",
    mfa_code: null,
  });
  const [unverified, setUnverified] = useState<boolean>(false);
  const [useMfa, setUseMfa] = useState<boolean>(false);

  const sendRequest = async () => {
    try {
      setSubmitting(true);
      const response = await apiAgent.Auth.requestResetPassword(formData);
      if (response.status === 200) {
        tMsg.success(`Your reset request has been submitted`);
        setSubmitted(true);
      }
    } catch (err: any) {
      if (err.response.status === 409) {
        setUnverified(true);
      } else if (err.response.status === 403) {
        setUseMfa(true);
        tMsg.info(
          "MFA authentication required to reset the password for this account"
        );
      } else {
        console.log(err);
        tMsg.error("Error sending request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!isValidEmail(formData.email)) {
      tMsg.error("Invalid email address");
      return;
    }
    if (
      useMfa &&
      (!formData.mfa_code || _.toString(formData.mfa_code).length < 6)
    ) {
      tMsg.error("Please enter 6 digit code");
      return;
    }
    sendRequest();
  };

  const updateFormData = (keyName: string, value: string | number) => {
    const newFormData = {
      ...formData,
      [keyName]: value,
    };
    setFormData(newFormData);
  };

  const UnverifiedElement = () => (
    <>
      <h1 className="font-bold text-lg">Unable to reset password</h1>
      <div className="text-sm my-4">
        Please contact an administrator for more information.
      </div>
    </>
  );

  const SubmittedElement = () => (
    <>
      <h1 className="font-bold text-lg">Your request has been submitted</h1>
      <div className="text-sm my-4">
        You should receive an email shortly containing a link to reset your MFA
        Token. This URL will expire within 2 hours. Please check your Junk/Spam
        mail folders.
      </div>
    </>
  );

  return (
    <div className="w-full h-full grid content-center">
      <div className="space-y-4 flex flex-col justify-center">
        <div className="font-medium place-self-center w-64 space-y-4 text-center">
          {unverified ? (
            <UnverifiedElement />
          ) : !submitted ? (
            <>
              <h1 className="font-bold text-lg">Reset your password</h1>
              <div className="text-sm">
                Enter the email address associated with your user account
              </div>
              <form className="grid place-items-center gap-8">
                <div className="my-4 space-y-2">
                  <div>Email</div>
                  <input
                    className="text-black p-1"
                    type={"email"}
                    value={formData.email}
                    onChange={(e) => {
                      e.preventDefault();
                      updateFormData("email", e.target.value);
                    }}
                    required
                    disabled={submitting}
                  />
                </div>
                {useMfa && (
                  <VerifyPinField
                    validating={submitting}
                    onChange={(e) => {
                      updateFormData("mfa_code", _.toNumber(e));
                    }}
                  />
                )}
                {submitting ? (
                  <Loader />
                ) : (
                  <Button
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                  >
                    Submit
                  </Button>
                )}
              </form>
            </>
          ) : (
            <SubmittedElement />
          )}
          <div className="text-primaryColor opacity-80 hover:opacity-100 transition pt-4 text-sm font-bold">
            <Link to="/auth">Return to login page</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestReset;
